import { useId, useMemo, useState } from 'react'
import {
  Archive,
  ArchiveRestore,
  CalendarClock,
  ChevronDown,
  Download,
  Eye,
  FileText,
  IdCard,
  Trash2,
} from 'lucide-react'
import { Badge, Button } from '@/components/ui'
import { FilePreviewModal, type PreviewFile } from '@/components/common/FilePreviewModal'
import { cn } from '@/lib/cn'
import { formatDate } from '@/lib/formatters'
import { downloadDocument, fetchDocumentFile, type UserDocument } from '@/api/services/documents.service'
import { toast } from 'sonner'
import {
  buildDocumentGroups,
  formatDocumentCategory,
  formatFileSize,
  type DocumentGroup,
} from '../utils/documentUtils'
import { documentTypeLabel } from '../utils/documentTypes'

function toPreviewFile(document: UserDocument): PreviewFile {
  return {
    id: document.id,
    name: document.originalName,
    mimeType: document.mimeType,
    size: document.size,
  }
}

/** Files added to a Drive folder: the folder says what they are, so no badge. */
const DRIVE_CATEGORY = 'drive'

const loadPreviewFile = (file: PreviewFile): Promise<Blob> => fetchDocumentFile(file.id)

/** Files being previewed in the viewer modal (a document group) plus the clicked file. */
interface DocumentPreviewState {
  title: string
  subtitle: string
  documents: UserDocument[]
  selectedId: string
}

/** True when an expiry date has passed. */
function isExpired(expiryDate: string | null): boolean {
  if (!expiryDate) return false
  const ts = Date.parse(expiryDate)
  return !Number.isNaN(ts) && ts <= Date.now()
}

/** What an employee document is (passport, visa…), when it says. */
function DocumentTypeBadge({ type }: { type?: string | null }): React.ReactElement | null {
  const label = documentTypeLabel(type)
  if (!label) return null
  return (
    <Badge variant="neutral" className="shrink-0 gap-1 text-xs">
      <IdCard className="h-3 w-3" aria-hidden />
      {label}
    </Badge>
  )
}

/** Expiry meta line ("Expires …") shown beneath a document group. */
function ExpiryMeta({ expiryDate }: { expiryDate: string | null }): React.ReactElement | null {
  if (!expiryDate) return null
  const expired = isExpired(expiryDate)
  return (
    <span className={cn(expired && 'font-medium text-red-600 dark:text-red-400')}>
      {expired ? 'Expired ' : 'Expires '}
      {/* Stored as the end of the chosen day in UTC: read that day, not the
          local time, which east of UTC is already the next morning. */}
      {formatDate(`${expiryDate.slice(0, 10)}T12:00:00`, 'dd MMM yyyy')}
    </span>
  )
}

export interface GroupedDocumentListProps {
  documents: UserDocument[]
  canArchive?: boolean
  canDelete?: boolean
  canEditExpiry?: boolean
  isArchivePending?: boolean
  isUnarchivePending?: boolean
  isDeletePending?: boolean
  onArchive?: (document: UserDocument) => void
  onUnarchive?: (documentId: string) => void
  onDelete?: (documentId: string) => void
  onEditExpiry?: (group: DocumentGroup) => void
  /** Names the expiry action; employee documents edit their type there too. */
  expiryActionLabel?: string
  /** Plain divided rows, for a list that isn't inside its own box. */
  bare?: boolean
  /** Leaves out the category badge, when the surroundings already say it. */
  hideCategory?: boolean
  /** Shows checkboxes when given. A group of files is selected as one. */
  selectedIds?: Set<string>
  onToggleSelected?: (ids: string[], selected: boolean) => void
}

function SelectBox({
  label,
  ids,
  selectedIds,
  onToggleSelected,
}: {
  label: string
  ids: string[]
  selectedIds?: Set<string>
  onToggleSelected?: (ids: string[], selected: boolean) => void
}): React.ReactElement | null {
  if (!selectedIds || !onToggleSelected) return null
  return (
    <input
      type="checkbox"
      aria-label={`Select ${label}`}
      checked={ids.every((id) => selectedIds.has(id))}
      onChange={(event) => onToggleSelected(ids, event.target.checked)}
      className="ml-3 h-4 w-4 shrink-0 cursor-pointer self-center rounded border-gray-300 accent-primary"
    />
  )
}

function DocumentFileActions({
  document,
  canArchive = false,
  canDelete = false,
  isArchivePending = false,
  isUnarchivePending = false,
  isDeletePending = false,
  onArchive,
  onUnarchive,
  onDelete,
  onPreview,
}: {
  document: UserDocument
  canArchive?: boolean
  canDelete?: boolean
  isArchivePending?: boolean
  isUnarchivePending?: boolean
  isDeletePending?: boolean
  onArchive?: (document: UserDocument) => void
  onUnarchive?: (documentId: string) => void
  onDelete?: (documentId: string) => void
  onPreview: (document: UserDocument) => void
}): React.ReactElement {
  return (
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        onClick={() => onPreview(document)}
        aria-label={`View ${document.originalName}`}
        title="View"
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-primary"
      >
        <Eye className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => {
          void downloadDocument(document).catch(() => {
            toast.error(`Could not download ${document.originalName}`)
          })
        }}
        aria-label={`Download ${document.originalName}`}
        title="Download"
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-primary"
      >
        <Download className="h-4 w-4" />
      </button>
      {canArchive && !document.archivedAt ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => onArchive?.(document)}
          disabled={isArchivePending}
          aria-label={`Archive ${document.originalName}`}
          className="text-gray-500 hover:text-amber-600 dark:text-gray-400"
        >
          <Archive className="h-4 w-4" />
        </Button>
      ) : null}
      {canArchive && document.archivedAt ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => onUnarchive?.(document.id)}
          disabled={isUnarchivePending}
          aria-label={`Unarchive ${document.originalName}`}
          className="text-gray-500 hover:text-primary dark:text-gray-400"
        >
          <ArchiveRestore className="h-4 w-4" />
        </Button>
      ) : null}
      {canDelete ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => onDelete?.(document.id)}
          disabled={isDeletePending}
          aria-label={`Delete ${document.originalName}`}
          className="text-gray-500 hover:text-danger dark:text-gray-400 dark:hover:text-danger"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  )
}

export function GroupedDocumentList({
  documents,
  canArchive = false,
  canDelete = false,
  canEditExpiry = false,
  isArchivePending = false,
  isUnarchivePending = false,
  isDeletePending = false,
  onArchive,
  onUnarchive,
  onDelete,
  onEditExpiry,
  expiryActionLabel = 'Set expiry date',
  bare = false,
  hideCategory = false,
  selectedIds,
  onToggleSelected,
}: GroupedDocumentListProps): React.ReactElement {
  const groupIdPrefix = useId()
  const groups = buildDocumentGroups(documents)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})
  const [preview, setPreview] = useState<DocumentPreviewState | null>(null)
  const previewFiles = useMemo(() => preview?.documents.map(toPreviewFile) ?? [], [preview])

  const toggleGroup = (groupId: string): void => {
    setOpenGroups((previous) => ({ ...previous, [groupId]: !previous[groupId] }))
  }

  const openPreview = (group: DocumentGroup, document: UserDocument): void => {
    setPreview({
      title: group.isGrouped ? group.title : document.originalName,
      subtitle: group.isGrouped
        ? `${group.fileCount} files — ${formatDocumentCategory(group.category)}`
        : formatDocumentCategory(group.category),
      documents: group.documents,
      selectedId: document.id,
    })
  }

  return (
    <>
    <ul
      className={
        bare
          ? 'divide-y divide-gray-100 border-y border-gray-100 dark:divide-gray-800 dark:border-gray-800'
          : 'space-y-2 rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-600 dark:bg-gray-800/50'
      }
    >
      {groups.map((group) => {
        const panelId = `${groupIdPrefix}-${group.id}`
        const isOpen = group.isGrouped && !!openGroups[group.id]
        const representativeDocument = group.representativeDocument

        return (
          <li
            key={group.id}
            className={cn(
              !bare &&
                'overflow-hidden rounded-lg border border-gray-200/80 bg-white dark:border-gray-700/80 dark:bg-gray-800/60',
              group.isArchived && 'opacity-70',
            )}
          >
            {group.isGrouped ? (
              <div className="flex items-stretch">
              <SelectBox
                label={group.title}
                ids={group.documents.map((document) => document.id)}
                selectedIds={selectedIds}
                onToggleSelected={onToggleSelected}
              />
              <button
                type="button"
                className="flex min-w-0 flex-1 flex-col gap-3 px-3 py-3 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:hover:bg-gray-800/80 sm:flex-row sm:items-center sm:justify-between"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={isOpen}
                aria-controls={panelId}
              >
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className="mt-0.5 rounded-md bg-gray-100 p-2 text-gray-500 dark:bg-gray-700/70 dark:text-gray-300">
                    <FileText className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {group.title}
                      </span>
                      <DocumentTypeBadge type={representativeDocument.documentType} />
                      {!hideCategory && group.category !== DRIVE_CATEGORY ? (
                        <Badge variant="neutral" className="shrink-0 text-xs">
                          {formatDocumentCategory(group.category)}
                        </Badge>
                      ) : null}
                      <Badge variant="info" className="shrink-0 text-xs">
                        {group.fileCount} files
                      </Badge>
                      {group.isArchived ? (
                        <Badge variant="warning" className="shrink-0 text-xs">
                          Archived
                        </Badge>
                      ) : null}
                      {isExpired(group.expiryDate) ? (
                        <Badge variant="danger" className="shrink-0 text-xs">
                          Expired
                        </Badge>
                      ) : null}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                      {representativeDocument.ownerName ? (
                        <span>{representativeDocument.ownerName}</span>
                      ) : null}
                      <span>{formatFileSize(group.totalSize)} total</span>
                      {group.latestCreatedAt ? (
                        <span>Updated {formatDate(group.latestCreatedAt, 'dd MMM yyyy')}</span>
                      ) : null}
                      {group.archivedFileCount > 0 && !group.isArchived ? (
                        <span>{group.archivedFileCount} archived</span>
                      ) : null}
                      <ExpiryMeta expiryDate={group.expiryDate} />
                    </div>
                  </div>
                </div>
                <span className="flex items-center justify-end text-xs font-medium text-gray-500 dark:text-gray-400">
                  <ChevronDown
                    className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
                    aria-hidden
                  />
                  <span className="ml-2">{isOpen ? 'Collapse' : 'Expand'}</span>
                </span>
              </button>
              {canEditExpiry ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onEditExpiry?.(group)}
                  aria-label={`${expiryActionLabel} for ${group.title}`}
                  title={expiryActionLabel}
                  className="mr-2 h-9 w-9 shrink-0 self-center px-0 text-gray-500 hover:bg-gray-100 hover:text-primary dark:text-gray-400 dark:hover:bg-gray-700/60"
                >
                  <CalendarClock className="h-4 w-4" />
                </Button>
              ) : null}
              </div>
            ) : (
              <div className="flex items-start">
              <SelectBox
                label={representativeDocument.originalName}
                ids={group.documents.map((document) => document.id)}
                selectedIds={selectedIds}
                onToggleSelected={onToggleSelected}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className="mt-0.5 rounded-md bg-gray-100 p-2 text-gray-500 dark:bg-gray-700/70 dark:text-gray-300">
                    <FileText className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openPreview(group, representativeDocument)}
                        className="truncate text-sm font-semibold text-primary hover:underline"
                      >
                        {representativeDocument.originalName}
                      </button>
                      <DocumentTypeBadge type={representativeDocument.documentType} />
                      {!hideCategory && representativeDocument.category !== DRIVE_CATEGORY ? (
                        <Badge variant="neutral" className="shrink-0 text-xs">
                          {formatDocumentCategory(representativeDocument.category)}
                        </Badge>
                      ) : null}
                      {representativeDocument.archivedAt ? (
                        <Badge variant="warning" className="shrink-0 text-xs">
                          Archived
                        </Badge>
                      ) : null}
                      {isExpired(group.expiryDate) ? (
                        <Badge variant="danger" className="shrink-0 text-xs">
                          Expired
                        </Badge>
                      ) : null}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                      {representativeDocument.ownerName ? (
                        <span>{representativeDocument.ownerName}</span>
                      ) : null}
                      <span>{formatFileSize(representativeDocument.size)}</span>
                      {representativeDocument.createdAt ? (
                        <span>{formatDate(representativeDocument.createdAt, 'dd MMM yyyy')}</span>
                      ) : null}
                      <ExpiryMeta expiryDate={group.expiryDate} />
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {canEditExpiry ? (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => onEditExpiry?.(group)}
                      aria-label={`${expiryActionLabel} for ${representativeDocument.originalName}`}
                      title={expiryActionLabel}
                      className="h-9 w-9 shrink-0 px-0 text-gray-500 hover:bg-gray-100 hover:text-primary dark:text-gray-400 dark:hover:bg-gray-700/60"
                    >
                      <CalendarClock className="h-4 w-4" />
                    </Button>
                  ) : null}
                  <DocumentFileActions
                    document={representativeDocument}
                    canArchive={canArchive}
                    canDelete={canDelete}
                    isArchivePending={isArchivePending}
                    isUnarchivePending={isUnarchivePending}
                    isDeletePending={isDeletePending}
                    onArchive={onArchive}
                    onUnarchive={onUnarchive}
                    onDelete={onDelete}
                    onPreview={(doc) => openPreview(group, doc)}
                  />
                </div>
              </div>
              </div>
            )}

            {group.isGrouped && isOpen ? (
              <div
                id={panelId}
                className="border-t border-gray-200 bg-gray-50/80 dark:border-gray-700 dark:bg-gray-900/20"
              >
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {group.documents.map((document) => (
                    <li
                      key={document.id}
                      className={cn(
                        'flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between',
                        document.archivedAt && 'opacity-70',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openPreview(group, document)}
                            className="truncate text-sm font-medium text-primary hover:underline"
                          >
                            {document.originalName}
                          </button>
                          {document.archivedAt ? (
                            <Badge variant="warning" className="shrink-0 text-xs">
                              Archived
                            </Badge>
                          ) : null}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                          <span>{formatFileSize(document.size)}</span>
                          {document.createdAt ? (
                            <span>{formatDate(document.createdAt, 'dd MMM yyyy')}</span>
                          ) : null}
                        </div>
                      </div>
                      <DocumentFileActions
                        document={document}
                        canArchive={canArchive}
                        canDelete={canDelete}
                        isArchivePending={isArchivePending}
                        isUnarchivePending={isUnarchivePending}
                        isDeletePending={isDeletePending}
                        onArchive={onArchive}
                        onUnarchive={onUnarchive}
                        onDelete={onDelete}
                        onPreview={(doc) => openPreview(group, doc)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </li>
        )
      })}
    </ul>

    {preview ? (
      <FilePreviewModal
        open
        onClose={() => setPreview(null)}
        title={preview.title}
        subtitle={preview.subtitle}
        files={previewFiles}
        initialFileId={preview.selectedId}
        loadFile={loadPreviewFile}
        onDownload={(file) => {
          const doc = preview.documents.find((d) => d.id === file.id)
          if (doc) return downloadDocument(doc)
        }}
      />
    ) : null}
    </>
  )
}

GroupedDocumentList.displayName = 'GroupedDocumentList'
